import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type Modifier,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  DND_DRAGGABLE_PROPS,
  DND_TOUCH_ACTIVATION,
  DrawerPinned,
  EmptyState,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  type Omittable,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Separator,
  sortableHandleProps,
  sortableStyle,
  TabsViewIcon,
  Text,
  useIsTouchDevice,
  useOptionalAutoFocus,
  ViewTabsRailButton,
} from "@salestrak/ui";

import type { EntityViewRow, EntityViewType } from "#app/lib/data";
import { ADDABLE_VIEW_TYPES, useAddView } from "./add-view-menu";
import { VIEW_TYPE_META, viewTabVisual } from "./view-type-meta";

/**
 * The tab strip's view roster: a quiet "{n} more…" trigger (ClickUp's
 * wording — the count of folded views, no chevron), seated after the last tab (it appears when the strip's overflow
 * folds views away — see the library's `TabsOverflow`), opening the whole
 * roster under a search. One
 * popup answers both of the strip's management questions — which views
 * exist, and in what order — where a clipped scroll edge answers neither:
 *
 *   - The search leads (the add-view popover's inset well, the
 *     selector-popover grammar) and filters the rows by name; the rows
 *     repeat the strip order. Two hairlines carry two meanings, the same two
 *     the strip draws:
 *     the STRONG one closes the pinned run, the regular one marks the fold —
 *     rows above it hold a tab, rows below live only here until room returns.
 *   - Rows drag to reorder — the same personal order the strip's own tab drag
 *     writes — so a view can be pulled above the fold or pushed below it
 *     without a precision drag across a crowded rail. Pinned rows drag among
 *     themselves only, above the strong hairline, as the strip's pinned run
 *     does: the drop targets and the travel are both the run's own rows. A
 *     filtered roster does not drag — a partial list cannot write a whole
 *     order — and draws neither hairline.
 *   - A press opens the view (the route also promotes a folded one back into
 *     the visible run so it does not fold straight back on the next measure).
 *   - The strip's one mark repeats at the row's end: the pin glyph on a
 *     pinned view. The default view is unmarked here as it is on the strip.
 *   - On a phone the strip's `+ View` lives in here instead (`onCreated`),
 *     whether or not the views fit — the trigger then stands even with
 *     nothing folded, and counts the action itself ("1 more…"). The
 *     addable types close the roster INLINE under their own hairline, a
 *     "New View" group of one row per type, so a view is one tap away with
 *     nowhere to step into and nothing to step back from. (A drilled "Add
 *     View" step with a back arrow was built first and reverted — Spencer,
 *     2026-09-29: navigation chrome inside a popup read as a dead end.)
 *
 * Two presentations of the one roster. On a phone it is a bottom SHEET —
 * the Filters sheet beside it on the same strip, and the way every drawer
 * of the app's drawers reads on a small screen: a title, the search on its
 * own band, the rows on the sheet's own gutter, dismissed by the bar, a
 * swipe or a tap outside. Everywhere else it is a popover under the
 * trigger. A popover, not a menu, on purpose: Base UI menu items activate
 * on click and move highlight with the pointer, which a drag gesture fights
 * — the same reason the column-settings panel and the Customize view Fields
 * page are popover surfaces. The trigger wears the rail-control treatment so
 * it reads as part of the strip, and it lights the active ink and selected
 * wash when the ACTIVE view is folded — the strip always shows where the
 * reader is.
 */

export type ViewOverflowMenuProps = Omittable<{
  /** Every view in the strip, in its current order — pinned first, folded
   *  ones last. */
  views: EntityViewRow[];
  /** How many of the trailing views are folded out of the strip. Zero
   *  when a phone seats the trigger only to host the folded `+ View`: that
   *  action then makes the trigger's "1 more…", and the roster draws no
   *  fold hairline. */
  hiddenCount: number;
  /** The mounted view's id: it lights the trigger when folded, and its row
   *  carries the check. */
  activeId?: string;
  /** The scope's pinned views (the rows' `isPinned`): seated first, above
   *  the strong hairline, marked with the pin, and dragged among themselves
   *  only. */
  pinnedIds?: readonly string[];
  /** A pick opens the view; the route also promotes a folded one back into
   *  the visible run so it does not fold straight back on the next measure. */
  onSelect: (view: EntityViewRow) => void;
  /** A row drag commits here with the roster's whole order — the pinned run,
   *  then the rest — exactly the sequence the strip's own `TabsList` drag
   *  writes. */
  onReorder: (order: string[]) => void;
  /** Folds the strip's `+ View` into the roster: a "New View" group of the
   *  addable types closes the rows, and a pick reports the created view
   *  here (the route owns navigation). Absent, the roster ends at its last
   *  view and the host seats its own add-view control. */
  onCreated?: (view: EntityViewRow) => void;
}>;

/** One roster row: hovering a row swaps the view's icon for the drag handle
 *  in the same 16px slot (the Customize view Fields page grammar). Touch
 *  keeps the icon — no hover, no reorder. The run marker is what the roster's
 *  drag clamp measures: a row travels only over its own run's rows. */
function SortableViewRow({
  view,
  active,
  pinned,
  sortable,
  onSelect,
}: {
  view: EntityViewRow;
  active: boolean;
  pinned: boolean;
  /** False while the roster is filtered: no grip, no drag. */
  sortable: boolean;
  onSelect: (view: EntityViewRow) => void;
}) {
  const isTouch = useIsTouchDevice();
  const [hovered, setHovered] = useState(false);
  const {
    attributes: { role: _role, tabIndex: _tabIndex, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: view.id, disabled: !sortable });

  const nodeRef = useCallback(
    (node: unknown) => setNodeRef(node as HTMLElement | null),
    [setNodeRef],
  );

  const tabVisual = viewTabVisual(view);
  const showGrip = sortable && !isTouch && (hovered || isDragging);

  return (
    <Box
      as="button"
      type="button"
      ref={nodeRef}
      direction="row"
      align="center"
      gap="sm"
      minHeight={32}
      paddingX="sm"
      rounded="sm"
      hoverBackground
      cursor={isDragging ? "grabbing" : "pointer"}
      // The row is the handle AND the popup's scrollable content, so it
      // keeps panning; the hold-activated touch sensor prevents the scroll
      // once a hold completes.
      touchAction="manipulation"
      {...DND_DRAGGABLE_PROPS}
      width="100%"
      data-view-run={runName(pinned)}
      style={sortableStyle(transform, {
        transition,
        isSorting,
        isDragging,
        axis: "y",
        dragOpacity: 0.3,
      })}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      // isDragging is still true when the browser fires the drop's click (the
      // re-render lands after) — the same disambiguation the Fields page and
      // KanbanCard rows use, so a drop never also opens the view.
      onClick={isDragging ? undefined : () => onSelect(view)}
      {...sortableHandleProps(attributes, listeners)}
    >
      <Box width={16} align="center" justify="center" flex="none">
        {showGrip ? (
          <Icon name="grip-vertical" size="sm" color="muted" />
        ) : (
          <TabsViewIcon
            color={tabVisual.color}
            icon={tabVisual.icon}
            variant={tabVisual.custom ? "glyph" : "compact-tile"}
          />
        )}
      </Box>
      <Box direction="column" shrink fill="flex" minWidth={0} align="start">
        <Text size="sm" truncate={1}>
          {view.name}
        </Text>
      </Box>
      {pinned ? (
        <Icon aria-label="Pinned" color="muted" name="pin" size="xs" />
      ) : null}
      {active ? <Icon name="check" size="sm" color="muted" /> : null}
    </Box>
  );
}

const NO_PINS: readonly string[] = [];

/** The two runs a roster row can belong to — the value of its run marker. */
const runName = (pinned: boolean) => (pinned ? "pinned" : "rest");

/** One row of the folded "New View" group: the type's tile in the roster's
 *  16px slot, its label on the roster's rung. */
function NewViewRow({
  type,
  disabled,
  onPick,
}: {
  type: EntityViewType;
  disabled: boolean;
  onPick: (type: EntityViewType) => void;
}) {
  const meta = VIEW_TYPE_META[type];
  return (
    <Box
      as="button"
      type="button"
      direction="row"
      align="center"
      gap="sm"
      minHeight={32}
      paddingX="sm"
      rounded="sm"
      hoverBackground
      cursor="pointer"
      touchAction="manipulation"
      width="100%"
      disabled={disabled}
      onClick={() => onPick(type)}
    >
      <Box width={16} align="center" justify="center" flex="none">
        <TabsViewIcon
          color={meta.color}
          icon={meta.tabIcon}
          variant="compact-tile"
        />
      </Box>
      <Text size="sm" truncate={1}>
        {meta.label}
      </Text>
    </Box>
  );
}

export function ViewOverflowMenu({
  views,
  hiddenCount,
  activeId,
  pinnedIds = NO_PINS,
  onSelect,
  onReorder,
  onCreated,
}: ViewOverflowMenuProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { canManage, creating, create } = useAddView(onCreated);
  const foldsAddView = onCreated !== undefined && canManage;
  // The caret lands in the search where a hardware keyboard is implied and
  // waits for its own tap on a coarse pointer.
  const searchAutoFocus = useOptionalAutoFocus();
  const onOpenChange = useCallback((isOpen: boolean) => {
    // Reset per open, not on close: a row reshuffle under the exit
    // transition would flash.
    if (isOpen) setQuery("");
    setOpen(isOpen);
  }, []);
  const handleCreate = useCallback(
    (type: EntityViewType) => {
      setOpen(false);
      void create(type);
    },
    [create],
  );

  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
  // Every row sorts, each inside its own run: the pinned run leads, the rest
  // follow, and the roster's order is the strip's.
  const ids = useMemo(() => views.map((view) => view.id), [views]);
  const pinnedCount = views.filter((view) => pinnedSet.has(view.id)).length;
  // The rows above this index hold a tab; the rest are the ones this trigger
  // counts.
  const foldIndex = views.length - hiddenCount;
  const activeFolded = views.some(
    (view, index) => index >= foldIndex && view.id === activeId,
  );
  const q = query.trim().toLowerCase();
  const filtering = q.length > 0;
  const shown = filtering
    ? views.filter((view) => view.name.toLowerCase().includes(q))
    : views;

  const sensors = useSensors(
    // 8px of travel before a drag arms, so a plain press stays a press; a
    // finger arms after a hold, so a swipe still scrolls the roster.
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );

  // A row drags inside its own run, as the strip's tabs do. The drop targets
  // are the run's rows — `closestCenter` over the whole roster would hand a
  // row held at its run's edge to the neighbouring run's nearest row — and
  // the travel is clamped to the run's rows, measured on drag start, so a
  // pinned row never hovers over the rest it cannot join.
  const runOf = useCallback(
    (id: UniqueIdentifier) => runName(pinnedSet.has(String(id))),
    [pinnedSet],
  );
  const listRef = useRef<HTMLDivElement | null>(null);
  const verticalBoundsRef = useRef<{
    minimumY: number;
    maximumY: number;
  } | null>(null);
  const captureVerticalBounds = useCallback(
    ({ active }: DragStartEvent) => {
      const rows = listRef.current?.querySelectorAll<HTMLElement>(
        `[data-view-run="${runOf(active.id)}"]`,
      );
      if (!rows?.length) {
        verticalBoundsRef.current = null;
        return;
      }
      verticalBoundsRef.current = {
        minimumY: rows.item(0).getBoundingClientRect().top,
        maximumY: rows.item(rows.length - 1).getBoundingClientRect().bottom,
      };
    },
    [runOf],
  );
  const restrictToRun = useCallback<Modifier>(
    ({ activeNodeRect, transform }) => {
      const bounds = verticalBoundsRef.current;
      if (!activeNodeRect || !bounds) return { ...transform, x: 0 };
      return {
        ...transform,
        x: 0,
        y: Math.min(
          bounds.maximumY - activeNodeRect.bottom,
          Math.max(bounds.minimumY - activeNodeRect.top, transform.y),
        ),
      };
    },
    [],
  );
  const collisionDetection = useCallback<CollisionDetection>(
    (args) => {
      const run = runOf(args.active.id);
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (container) => runOf(container.id) === run,
        ),
      });
    },
    [runOf],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      verticalBoundsRef.current = null;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      // Runs are contiguous in the roster, so a move inside one keeps both
      // whole.
      if (runOf(active.id) !== runOf(over.id)) return;
      const from = ids.indexOf(String(active.id));
      const to = ids.indexOf(String(over.id));
      if (from === -1 || to === -1) return;
      onReorder(arrayMove(ids, from, to));
    },
    [ids, runOf, onReorder],
  );

  const handleSelect = useCallback(
    (view: EntityViewRow) => {
      setOpen(false);
      onSelect(view);
    },
    [onSelect],
  );

  // The trigger counts everything it holds that the strip does not show: the
  // folded views, plus the folded `+ View` itself — so a phone with every
  // tab fitting still reads "1 more…", never a bare "More…".
  const moreCount = hiddenCount + (foldsAddView ? 1 : 0);

  const trigger = (
    <ViewTabsRailButton active={activeFolded} aria-label={`${moreCount} more`} />
  );

  const search = (
    <InputGroup>
      <InputGroupAddon>
        <Icon name="search" />
      </InputGroupAddon>
      <InputGroupInput
        aria-label="Search views"
        autoFocus={searchAutoFocus}
        onValueChange={setQuery}
        placeholder="Search views..."
        type="search"
        value={query}
      />
    </InputGroup>
  );

  // The rows, in both presentations: the roster under its search, then —
  // when the host folded its add-view control in here — the New View group.
  const rows = (
    <Box padding="xs" ref={listRef}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        modifiers={[restrictToRun]}
        onDragStart={captureVerticalBounds}
        onDragCancel={() => {
          verticalBoundsRef.current = null;
        }}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {filtering && shown.length === 0 ? (
            <Box align="center" justify="center" paddingY="md">
              <EmptyState size="xs" title="No views found" />
            </Box>
          ) : null}
          {shown.map((view, index) => (
            <Fragment key={view.id}>
              {/* The strong hairline closes the pinned run; the regular
                  one marks the fold. When the fold lands right after the
                  pins, the boundary is drawn once, at the louder rung.
                  A filtered roster keeps neither: its rows are a
                  selection, not the strip's runs. */}
              {filtering ? null : index === pinnedCount && pinnedCount > 0 ? (
                <Box paddingY="2xs">
                  <Separator tone="strong" />
                </Box>
              ) : index === foldIndex && index > 0 ? (
                <Box paddingY="2xs">
                  <Separator />
                </Box>
              ) : null}
              <SortableViewRow
                view={view}
                active={view.id === activeId}
                pinned={pinnedSet.has(view.id)}
                sortable={!filtering}
                onSelect={handleSelect}
              />
            </Fragment>
          ))}
        </SortableContext>
      </DndContext>
      {foldsAddView ? (
        <>
          {/* The strip's folded `+ View`: a hairline closes the roster, and
              the addable types follow as rows of their own group — the
              same rung as the views above them, so the group reads as "the
              views this scope could have", not a second control. A search
              filters views, never types: the group stands through a
              filter, so a reader who found nothing can still add. */}
          <Box paddingY="2xs">
            <Separator />
          </Box>
          <Box paddingX="sm" paddingTop="xs" paddingBottom="2xs">
            <Text size="xs" variant="muted" weight="medium">
              New View
            </Text>
          </Box>
          {ADDABLE_VIEW_TYPES.map((type) => (
            <NewViewRow
              key={type}
              disabled={creating}
              onPick={handleCreate}
              type={type}
            />
          ))}
        </>
      ) : null}
    </Box>
  );

  return (
    // `responsive`: a bottom sheet on a phone, captioned "Views", the search
    // pinned above the roster.
    <Popover open={open} onOpenChange={onOpenChange} responsive>
      <PopoverTrigger render={trigger}>{moreCount} more…</PopoverTrigger>
      {/* Menu-action width (252px): the rows are menu-row shaped, so the
          popup takes the menu's rung rather than hugging the longest name. */}
      <PopoverPopup
        align="start"
        className="w-63"
        padding="none"
        sheetTitle="Views"
        side="bottom"
      >
        {/* The inset search well of the add-view popover: no bottom
            padding, the list below carries its own 8px gutter. */}
        <DrawerPinned>
          <Box paddingTop="sm" paddingX="sm">
            {search}
          </Box>
        </DrawerPinned>
        {rows}
      </PopoverPopup>
    </Popover>
  );
}
